<template>
  <div id="app-scroll-wrapper">
    <sdui-notifications />
    <transition :name="transitionName"
                appear
                @before-enter="onBeforeEnterTransition"
                @after-enter="onAfterEnterLeaveTransition"
    >
      <component :is="layout" />
    </transition>

    <div id="reload-indicator">
      <div class="custom-spinner p2" />
      <!-- pre-load error image -->
      <img v-show="false"
           src="/img/miscellaneous/ui/no-connection.svg"
           alt=""
           loading="lazy"
      />
    </div>
    <router-side-video />

    <SModal
      v-model="permissionModal.showPermissionModal"
      :header-title="$t('permissions.android.modal.title')"
      no-close-on-backdrop
      no-close-on-esc
      :show-footer-gradient="false"
      @close="requestPermissionAgain()"
    >
      <permission-modal
        v-if="permissionModal.showPermissionModal"
        :requested-permission="permissionModal.requestedPermission"
        @close="requestPermissionAgain()"
      />
    </SModal>
  </div>
</template>

<script>
import Users from 'src/api/users.js'

import native from './config/native.js'

import Sdui from 'src/layout/Sdui.vue'
import Default from 'src/layout/Default.vue'
import SduiWithScrollContainer from 'src/layout/SduiWithScrollContainer.vue'
import SduiAuthenticator from 'src/layout/SduiAuthenticator.vue'
import SchoolManagement from 'src/layout/SchoolManagement.vue'

import RouterSideVideo from 'src/components/miscellaneous/ui/notch/RouterSideVideo.vue'
import SduiNotifications from 'src/components/miscellaneous/ui/SduiNotifications.vue'

import multiUserMixin from './mixins/authentification/multiUserMixin.js'
import PermissionModal from 'src/components/miscellaneous/ui/modals/PermissionModal.vue'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import registrationMixin from 'src/mixins/authentification/registrationMixin.js'

import { useHead } from '@/composables/utils/useHead.ts'
import { useSduiI18n } from '@/composables/useSduiI18n.ts'
import { useStore } from '@/composables/useStore.ts'
import {computed, onMounted, watch} from 'vue'
import { useRoute } from 'vue-router'
import { useChunkPreloadErrorHandling } from '@/composables/utils/useChunkPreloadErrorHandling.ts'
import { useLiveUpdate } from '@/config/setup/useLiveUpdate.ts'

export default {
  // TODO VUE3: This is a mix of Options API and Composition API which should be removed by refactoring
  // the whole component to Composition API. Right now it's needed because of useHead.
  setup() {
    useChunkPreloadErrorHandling()
    const { t, tm } = useSduiI18n()
    const { store } = useStore()
    const { setHead } = useHead()
    const route = useRoute()

    const transitionName = computed(() => {
      return store.state.transitionName
    })

    const translatedHeaderTags = computed(() => {
      // vue-meta attributes need either be filled or undefined to take the fallback from index.html
      // _vars are defined a bit later
      if (!tm('_vars')) return undefined

      // Screenreaders need a unique page title for each page
      // We also don't put "Sdui" in the title anymore so it doesnt get repeated on every route change
      // Everything will remain unchanged for non-screenreader users
      const screenReaderTitle = route.meta.noSchoolHeader || route.meta.headerText
      const isScreenReaderActive = store.state.isScreenReaderActive

      return {
        title: isScreenReaderActive && screenReaderTitle ? t(screenReaderTitle) : t('global.app_name'),
        description: t('login.login_sign_in.page_default_description'),
        meta: {
          appName: t('global.app_name'),
          companyName: t('global.company_name'),
          app_url: t('global.app_url'),
        },
        link: {
          copyright: t('settings.index.imprint.link'),
        },
      }
    })

    const meta = computed(() => {
      /* ATTENTION!
         This method replaces the meta tags at RUNTIME which means link crawlers will not pick up the changed tags if
         they differ from the DEFAULT branding.
         In the build process we only differ between DEFAULT and PUPIL. Those two brandings will overwrite the meta tags
         during the BUILDTIME (quick-fix, see vite.config.ts).
         We still have to do this so the user sees the correct title, description, etc. in case any other branding is
         used.
       */
      const brandingConfig = store.getters['schoolBranding/getBrandingConfig'].meta
      return {
        meta: [
          ...brandingConfig.meta,
          { name: 'application-name', content: translatedHeaderTags.value?.meta.appName },
          { name: 'author', content: translatedHeaderTags.value?.meta.companyName },
          { name: 'copyright', content: translatedHeaderTags.value?.meta.companyName },
          { name: 'description', content: translatedHeaderTags.value?.description },
          { name: 'og:title', content: translatedHeaderTags.value?.title },
          { name: 'og:url', content: translatedHeaderTags.value?.meta.app_url },
          { name: 'og:author', content: translatedHeaderTags.value?.meta.companyName },
          { name: 'og:description', content: translatedHeaderTags.value?.description },
          { name: 'twitter:url', content: translatedHeaderTags.value?.meta.app_url },
          { name: 'twitter:title', content: translatedHeaderTags.value?.title },
          { name: 'twitter:description', content: translatedHeaderTags.value?.description },
        ],
        link: [...brandingConfig.link, { rel: 'copyright', href: translatedHeaderTags.value?.link.copyright }],
        title: translatedHeaderTags.value?.title,
      }
    })

    setHead(meta.value)

    watch(
      () => route.name,
      (newName, oldName) => {
        if (oldName !== 'login-sign-in') return
        // Update meta tags when user just logged in. This is because on login page we change the title and description
        setHead(meta.value)
      }
    )

    onMounted(async () => {
      await useLiveUpdate()
    })

    return {
      transitionName
    }
  },

  name: 'App',
  components: {
    SduiNotifications,
    RouterSideVideo,
    sdui: Sdui,
    Default,
    SchoolManagement,
    SduiAuthenticator,
    PermissionModal,
    SduiWithScrollContainer,
  },

  mixins: [registrationMixin, isMobileDeviceMixin, multiUserMixin],

  data: () => ({
    // todo: I´m not sure if this is bad for performance
    window,
    layout: '',
    cacheCheckupInterval: null,
    permissionModal: {
      showPermissionModal: false,
      requestedPermission: '',
    },

    // a11y relevant variables
    lastInteractionWasPointer: false,
    keyboardNavigationTimeout: null,
    isScreenReaderActive: false,
    currentFocusEventTimestamp: 0,
    lastFocusEventTimestamp: 0,
  }),

  computed: {
    isAuthenticated() {
      return !!this.$store.state.authToken || !!this.$store.dispatch('loadAuthToken', null, { root: true })
    },
  },

  methods: {
    // Switching between layouts triggers a transition which is animated, so it has a few ms of delay.
    // During this transition a scrollbar appears for a very short moment because both layouts are rendered in
    // the DOM (one has v-leave class, other one has v-enter class). This is especially visible if the routes
    // don't set a transitionName.
    // To prevent this flickering we set the body to overflow: hidden during the transition and restore it after
    // the animation has finished.
    onBeforeEnterTransition() {
      document.body.style && (document.body.style.overflow = 'hidden')
    },

    onAfterEnterLeaveTransition() {
      document.body.style && (document.body.style.overflow = 'auto')
    },

    async getPushData(pushData) {
      native.resetNativeBadge()

      const data = typeof pushData === 'string' ? JSON.parse(pushData || '{}') : pushData
      const cacheToken = data.cache_token
      const deviceToken = this.$store?.state?.device?.firebaseToken

      const response = await Users.getPushExtra(cacheToken, deviceToken)
      const responseData = response?.data ?? {}

      const userId = responseData?.user_id
      const uri = responseData?.uri

      if (userId && this.hasMultipleAccounts) {
        return this.$store.dispatch('multiUsers/selectUser', { userId, path: uri, showNotification: true }, { root: true })
      }

      // push redirect
      if (!uri) return
      this.$router.push(uri)
    },

    getPermissionRequestData(permissionRequest) {
      const data = JSON.parse(permissionRequest.data || '{}')
      this.permissionModal = { showPermissionModal: true, requestedPermission: data?.requestedPermission || '' }
    },

    requestPermissionAgain() {
      this.permissionModal = { showPermissionModal: false, requestedPermission: '' }
    },

    async setNativeShortcuts() {
      const shortcuts = [
        { type: 'com.sdui.sdui.login-start-help', title: this.$t('global.shortcuts.help') },
        // { type: 'com.sdui.sdui.more', title: this.$t('global.shortcuts.more') }, TODO
        { type: 'com.sdui.sdui.settings-overview-01', title: this.$t('global.shortcuts.account') },
      ]

      if (this.isAuthenticated) return native.setNativeShortcuts(shortcuts)
      return native.setNativeShortcuts([])
    },

    async onFocus() {
      await this.setUserFontPreference()

      // reset Badges
      native.resetNativeBadge()

      // save push token
      setTimeout(() => this.$store.dispatch('savePushToken'), 1 * 1000)

      if (!this.$me) return

      // mobile only: re-fetch activity summary after app is focused again (it was put to background before)
      this.isMobile && this.$store.dispatch('users/getActivitySummary')

      // when user is logged in and has 'PENDING' state for push-notification consent, re-ask for consent
      native.registerPushNotification()
    },

    // Ensure focussed element is in view for keyboard users
    // As of this date: CSS "scroll-margin" does not work on iOS, so this is our js solution
    // FYI: "scroll-margin" and this JS solution does not work in Screenreader!
    ensureFocusIsInView(event) {
      if (!this.$store.state.isKeyboardNavigationActive) return

      const el = event.target
      if (!(el instanceof Element)) return

      // Prevent unnecessary scrolling of child elements inside the sticky elements themself
      if (el.closest('[data-prevent-focus-scroll]')) return

      const rect = el.getBoundingClientRect()
      const timetableBottomBar = document.querySelector('.timetable-bottom-button') // timetable bottom buttons
      const scrollMarginTop = 60 // because of sticky nav on severel pages
      const scrollMarginBottom = timetableBottomBar ? 120 : 60 // because sticky bottom nav on several pages
      const visualBuffer = 20 // breathing room to the sticky element

      const viewportHeight = window.innerHeight

      const isOverlappedAtTop = rect.top < scrollMarginTop
      const isOverlappedAtBottom = rect.bottom > viewportHeight - scrollMarginBottom

      // Element is hidden behind top sticky nav
      if (isOverlappedAtTop) {
        const scrollContainer = el.closest('.scrollable-container') || window // scrollable-container is used for the timetable
        return scrollContainer.scrollBy({
          top: rect.top - scrollMarginTop - visualBuffer,
          behavior: 'instant',
        })
      }

      // Element is hidden behind sticky footer
      if (isOverlappedAtBottom) {
        const scrollContainer = el.closest('.scrollable-container') || window // scrollable-container is used for the timetable
        return scrollContainer.scrollBy({
          top: rect.bottom - (viewportHeight - scrollMarginBottom) + visualBuffer,
          behavior: 'instant',
        })
      }
    },

    handleFocus(event) {
      this.ensureFocusIsInView(event)

      // Focus event was either triggered by keyboard, or programmatically by calling .focus(). We need to distinguish
      // between the two to avoid showing the focus-frame for mouse/touch users.
      // Unfortunately, not all mobile OS trigger a keydown event for TAB keypress, so we have to rely on the focus event.

      // TODO find a better solution for this
      // As a quick workaround, we assume that programmatic focus events are triggered in quick succession, while keyboard
      // focus events are not (at least not quicker than TIME_THRESHOLD).
      const TIME_THRESHOLD = 100
      this.currentFocusEventTimestamp = Date.now()
      const diff = this.currentFocusEventTimestamp - this.lastFocusEventTimestamp
      const isProgrammaticFocus = diff < TIME_THRESHOLD
      this.lastFocusEventTimestamp = this.currentFocusEventTimestamp
      const isE2ETest = window.Cypress || window.Playwright

      // Infer keyboard navigation if no recent pointer interaction
      // do not add keyboard-navigation class if screen reader is active (SR provide their own focus indicators)
      // do not add keyboard-navigation class if focus event was triggered programmatically
      // do not add keyboard-navigation class if keyboard navigation is already active
      // do not add keyboard-navigation class in e2e tests
      if (
        this.lastInteractionWasPointer ||
        this.isScreenReaderActive ||
        isProgrammaticFocus ||
        this.$store.state.isKeyboardNavigationActive ||
        isE2ETest
      )
        return

      document?.body?.classList?.add('keyboard-navigation')
      this.$store.commit('SET_IS_KEYBOARD_NAVIGATION_ACTIVE', true)
    },

    // Handle pointer input (mouse or touch)
    handlePointerInput(ev) {
      // Special treatment for apple: Some key inputs such as spacebar will trigger mousedown and touchstart events,
      // which would remove the keyboard-navigation class. Difference between a real mousedown/touchstart event and
      // one triggered by key is that key events are cancelable.
      if (ev.cancelable && this.$store.state.isIOS) return

      this.lastInteractionWasPointer = true
      document?.body?.classList?.remove('keyboard-navigation')
      this.$store.commit('SET_IS_KEYBOARD_NAVIGATION_ACTIVE', false)

      // Reset the flag after a short delay to avoid blocking Tab detection
      clearTimeout(this.keyboardNavigationTimeout)
      this.keyboardNavigationTimeout = setTimeout(() => {
        this.lastInteractionWasPointer = false
      }, 250)
    },

    async setUserFontPreference() {
      const { fontSize, boldTextEnabled } = await native.getUserPreferredFontSettings()

      const a11yFontWeightClass = 'font-weight--increase'
      boldTextEnabled ? document.body.classList.add(a11yFontWeightClass) : document.body.classList.remove(a11yFontWeightClass)

      if (!fontSize) return
      document.documentElement.style.setProperty('--default-font-size', fontSize)
    },

    async handleSwitchedAccounts() {
      if (!this.$route.query.switchedAccounts || !this.$me) return
      await this.$sleep(500)
      this.$toastNotify({
        type: 'info',
        text: this.$me.school.name,
        title: this.$t('ui.notifications.switched_accounts.title'),
        data: {
          channelName: this.$me.school.name,
          icon: 'icon-home',
        },
        duration: 5000,
        position: 'bottom right',
        group: 'update',
      })
      // remove "switchedAccounts" from query
      this.$router.replace({
        name: this.$route.name,
        query: { ...this.$route.query, switchedAccounts: undefined },
        hash: this.$route.hash,
      })
    },
  },

  watch: {
    '$route.meta.layout': {
      handler: function (val) {
        this.layout = val || 'default'
      },

      immediate: true,
    },

    '$route.query.switchedAccounts': {
      handler: function (bool) {
        if (!bool) return
        this.handleSwitchedAccounts()
      },

      immediate: true
    },

    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  async created() {
    await native.initialize()
    await this.setNativeShortcuts()
    await native.listenNativeShortcuts((pathName) => this.$router.push({ name: pathName }))
    await native.listenPushNotificationsAction((pushData) => this.getPushData(pushData), window)

    // handle permission requests for Audio in Android for displaying rationale dialog
    window.setAndroidPermissionRequestData = this.getPermissionRequestData

    // for ios native app, when the app is not closed
    window.addEventListener('focus', this.onFocus)
    window.addEventListener('pageshow', this.onFocus)
    document.addEventListener('resume', this.onFocus, false)

    /*
    We only want our focus-frame to be visible for keyboard users, not mouse users.
    But due to our focus-frame being visible when ".focus()" gets called (which also gets called by bootstrap for b-dropdown or b-modal).
    We have to set a body class to detect if the user is navigating with the keyboard or not.
    */
    document.addEventListener('mousedown', this.handlePointerInput, true) // Detect mouse input
    document.addEventListener('touchstart', this.handlePointerInput, true) // Detect touch input
    document.addEventListener('focus', this.handleFocus, true) // Detect focus changes
  },

  async mounted() {
    await native.notifyFrontendReady()
    await this.setUserFontPreference()
  },
}
</script>

<style lang="scss">
#app {
  overflow: hidden;
  width: 100%;
  height: 100%;

  * {
    // Fix overlapping glyphs for pairs like "?!" caused by the webfont's contextual
    // alternates (calt) substitution. Common ligatures (e.g. "fi") stay enabled.
    font-variant-ligatures: common-ligatures no-contextual;
  }
}

#app-scroll-wrapper {
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  height: 100%;

  .modal-open & {
    overflow: hidden;
  }
}

#reload-indicator {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  align-items: center;
  justify-content: space-around;
  background: rgba($color-white-rgb, 0.75);
  display: none;
}

body.reloading #reload-indicator {
  display: flex;
}
</style>
